/**
 * G2eGente - Diagnósticos NR01 (Riscos Psicossociais)
 * Lista e gerencia diagnósticos NR01 da empresa
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { nr01API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Plus,
  BarChart3,
  FileText,
  Users,
  Calendar,
  Loader2,
  Play,
  Pause,
  Eye,
  Pencil,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  Building2,
  Brain,
  ArrowLeft,
  Link2,
  Copy,
  Check,
  QrCode,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700', icon: FileText },
  ativo: { label: 'Ativo', color: 'bg-emerald-100 text-emerald-700', icon: Play },
  encerrado: { label: 'Encerrado', color: 'bg-slate-200 text-slate-600', icon: Pause }
};

export default function NR01Diagnostics() {
  const navigate = useNavigate();
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    fetchDiagnostics();
  }, []);

  const fetchDiagnostics = async () => {
    try {
      const { data } = await nr01API.list();
      setDiagnostics(data);
    } catch (error) {
      toast.error('Erro ao carregar diagnósticos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir este diagnóstico? Todas as respostas serão perdidas.')) return;
    
    try {
      await nr01API.delete(id);
      toast.success('Diagnóstico excluído');
      fetchDiagnostics();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleActivate = async (diag, e) => {
    e.stopPropagation();
    try {
      await nr01API.update(diag.id, { status: 'ativo' });
      toast.success('Diagnóstico ativado! Os funcionários já podem responder.');
      fetchDiagnostics();
    } catch (error) {
      toast.error('Erro ao ativar diagnóstico');
    }
  };

  const handleClose = async (diag, e) => {
    e.stopPropagation();
    try {
      await nr01API.update(diag.id, { status: 'encerrado' });
      toast.success('Diagnóstico encerrado');
      fetchDiagnostics();
    } catch (error) {
      toast.error('Erro ao encerrar diagnóstico');
    }
  };
  
  const handleCopyLink = (codigo, e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/p/nr01/${codigo}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(codigo);
    toast.success('Link copiado! Compartilhe com quem desejar.');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  const filteredDiagnostics = filter === 'todos' 
    ? diagnostics 
    : diagnostics.filter(d => d.status === filter);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/company')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-amber-600" />
              Diagnóstico NR-01
            </h1>
            <p className="text-slate-500 mt-1">Avaliação de Riscos Psicossociais no Trabalho</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/company/nr01/new')}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="new-nr01-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Diagnóstico
        </Button>
      </div>

      {/* Botão Evolução */}
      <div className="flex justify-end mb-4">
        <Button 
          variant="outline" 
          onClick={() => navigate('/company/nr01/evolucao')}
          className="border-rose-300 text-rose-600 hover:bg-rose-50"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Ver Evolução Histórica
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-semibold text-amber-800 mb-1">Conformidade com a NR-01</h4>
              <p className="text-sm text-amber-700">
                Este diagnóstico atende à <strong>Portaria MTE 1.419/2024</strong> que inclui riscos psicossociais 
                no Programa de Gerenciamento de Riscos (PGR). As respostas são <strong>100% anônimas</strong> e 
                focam no <strong>ambiente de trabalho</strong>, não em indivíduos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'todos', label: 'Todos' },
          { key: 'ativo', label: 'Ativos' },
          { key: 'rascunho', label: 'Rascunhos' },
          { key: 'encerrado', label: 'Encerrados' }
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'bg-amber-600' : ''}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Empty State */}
      {filteredDiagnostics.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <ShieldCheck className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {filter === 'todos' ? 'Nenhum diagnóstico criado' : `Nenhum diagnóstico ${filter}`}
            </h3>
            <p className="text-slate-500 text-center max-w-md mb-4">
              O diagnóstico NR-01 avalia riscos psicossociais no ambiente de trabalho através 
              de 35 perguntas validadas cientificamente (HSE-IT + COPSOQ).
            </p>
            {filter === 'todos' && (
              <Button onClick={() => navigate('/company/nr01/new')} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Diagnóstico
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diagnostic Cards */}
      <div className="grid gap-4">
        {filteredDiagnostics.map(diag => {
          const statusConfig = STATUS_CONFIG[diag.status] || STATUS_CONFIG.rascunho;
          const StatusIcon = statusConfig.icon;
          const adesao = diag.total_elegivel > 0 
            ? Math.round((diag.total_respostas / diag.total_elegivel) * 100)
            : 0;

          return (
            <Card 
              key={diag.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(diag.status === 'rascunho' 
                ? `/company/nr01/${diag.id}/edit` 
                : `/company/nr01/${diag.id}/results`
              )}
              data-testid={`nr01-card-${diag.id}`}
            >
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-800">{diag.titulo}</h3>
                      <Badge className={statusConfig.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                    
                    {diag.descricao && (
                      <p className="text-slate-500 text-sm mb-3 line-clamp-2">{diag.descricao}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {diag.setores?.length || 0} setores
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {diag.perguntas?.length || 35} perguntas
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {diag.total_respostas}/{diag.total_elegivel} ({adesao}%)
                      </span>
                      {diag.data_fim && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Até {new Date(diag.data_fim).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                    
                    {diag.status === 'ativo' && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                          <span>Taxa de adesão</span>
                          <span>{adesao}%</span>
                        </div>
                        <Progress value={adesao} className="h-2" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    {diag.status === 'rascunho' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/company/nr01/${diag.id}/edit`); }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={(e) => handleActivate(diag, e)}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Ativar
                        </Button>
                      </>
                    )}
                    
                    {diag.status === 'ativo' && (
                      <>
                        {diag.link_publico && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              onClick={(e) => handleCopyLink(diag.link_publico, e)}
                              data-testid={`copy-link-${diag.id}`}
                            >
                              {copiedLink === diag.link_publico ? (
                                <Check className="w-4 h-4 mr-1" />
                              ) : (
                                <Link2 className="w-4 h-4 mr-1" />
                              )}
                              {copiedLink === diag.link_publico ? 'Copiado!' : 'Link'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-purple-600 border-purple-300 hover:bg-purple-50"
                              onClick={(e) => { e.stopPropagation(); navigate(`/company/nr01/${diag.id}/qrcode/${diag.link_publico}`); }}
                              data-testid={`qr-code-${diag.id}`}
                            >
                              <QrCode className="w-4 h-4 mr-1" />
                              QR Code
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/company/nr01/${diag.id}/results`); }}
                        >
                          <BarChart3 className="w-4 h-4 mr-1" />
                          Resultados
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={(e) => handleClose(diag, e)}
                        >
                          <Pause className="w-4 h-4 mr-1" />
                          Encerrar
                        </Button>
                      </>
                    )}
                    
                    {diag.status === 'encerrado' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/company/nr01/${diag.id}/results`); }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Resultados
                      </Button>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDelete(diag.id, e)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 7 Dimensions Info */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Brain className="w-5 h-5 text-amber-600" />
            As 7 Dimensões Avaliadas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { nome: 'Demandas', desc: 'Carga de trabalho, prazos, pressão', icon: '⚡' },
              { nome: 'Controle', desc: 'Autonomia, flexibilidade, participação', icon: '🎯' },
              { nome: 'Apoio Gerencial', desc: 'Suporte e feedback da chefia', icon: '👔' },
              { nome: 'Apoio Colegas', desc: 'Cooperação entre pares', icon: '🤝' },
              { nome: 'Relacionamentos', desc: 'Conflitos, assédio, tensões', icon: '⚠️' },
              { nome: 'Clareza de Papel', desc: 'Responsabilidades definidas', icon: '📋' },
              { nome: 'Mudanças', desc: 'Comunicação de mudanças', icon: '🔄' },
            ].map((dim, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span>{dim.icon}</span>
                  <span className="font-medium text-slate-800 text-sm">{dim.nome}</span>
                </div>
                <p className="text-xs text-slate-500">{dim.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
